import { useEffect, useState } from "react";
import { Wrench } from "lucide-react";
import {
  descreverNivel,
  descreverPrevisao,
  useJanelaDeManutencao,
} from "@/lib/maintenance-window";

/**
 * O aviso de manutenção deliberada, nas telas do escritório (TUT-145 fatia 1e).
 *
 * Fecha a TUT-145: 1a pôs os três níveis no banco, 1b fez a guarda bloquear,
 * 1c deu o interruptor à API, 1d a tela do Console — e faltava a metade que
 * importa para quem **usa** o sistema. Até aqui, ligar a manutenção deixava o
 * escritório vendo operações falharem sem explicação nenhuma.
 *
 * ## Três decisões, e nenhuma é estética
 *
 * **1. Ele não é fechável.** Um "x" transformaria o aviso em algo que se fecha
 * sem ler, e a pessoa voltaria a tentar salvar dois minutos depois. Ele sai
 * quando a manutenção sai — por fato, não por gesto nem por tempo.
 *
 * **2. Ele diz o EFEITO, não o nível.** `READ_ONLY` não significa nada para
 * quem está com um laudo na tela; *"nada pode ser salvo agora"* responde à
 * pergunta que a pessoa tem de verdade, que é *"por que não salvou?"*.
 *
 * **3. A previsão vencida é dita em voz alta.** Mesma regra do Console: uma
 * previsão sozinha vira mentira em vinte minutos, e um aviso em que o
 * escritório deixou de confiar contamina o aviso seguinte.
 *
 * O elemento é `<output>`, e não `<div role="alert">`: `output` já carrega a
 * semântica de `status`, e `alert` interromperia o leitor de tela no meio da
 * frase — isto é uma condição CONTÍNUA, não um evento. (O `role` explícito
 * saiu porque o `jsx-a11y` recusa `role` quando existe tag equivalente, e ele
 * está certo: a tag é a forma canônica.)
 */
export function MaintenanceBanner() {
  const janela = useJanelaDeManutencao();
  // O relógio corre só enquanto há janela — sem manutenção não há o que
  // vigiar, e um `setInterval` permanente acordaria a aba à toa. Com janela,
  // a previsão precisa VENCER na tela sem ninguém recarregar.
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    if (!janela) return;
    const tique = setInterval(() => setAgora(new Date()), 30_000);
    return () => clearInterval(tique);
  }, [janela]);

  if (!janela) return null;

  return (
    <output
      data-testid="maintenance-banner"
      // `--warning-border` e `--warning-surface` NUNCA existiram no
      // `tailwind.css`: o aviso pedia dois tokens inventados e caía nos
      // fallbacks, então nasceu neutro em vez de âmbar e ninguém viu — o
      // fallback é exatamente o que esconde o erro. Aqui ficam os tokens reais
      // que ele já vinha usando, para o pixel não mudar junto com o conserto.
      // Deixar o aviso âmbar de verdade é outra decisão, e ela traz dois pares
      // de contraste novos (`--text` e `--muted` sobre `--warning-highlight`);
      // está registrada no delivery-log, não escondida num fallback.
      className="sticky top-0 z-40 flex items-start gap-3 border-b border-[var(--border-strong)] bg-[var(--surface-2)] px-4 py-3 text-[var(--text)] sm:px-6"
    >
      <Wrench aria-hidden="true" className="mt-[0.15rem] shrink-0" />
      <div className="min-w-0">
        <strong className="block">Manutenção em andamento</strong>
        <span className="block text-corpo">{descreverNivel(janela.level)}</span>
        <span className="block text-corpo text-[var(--muted)]">
          {descreverPrevisao(janela, agora)}
          {janela.observacao ? ` ${janela.observacao}` : ""}
        </span>
      </div>
    </output>
  );
}
